import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { apiClient, axiosInstance } from "../services/apiClient";
import { UserRegister } from "../generated/api-client";
import { toolsApi } from "../services/toolsApi";

interface User {
  id: string;
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  role?: string;
  is_active: boolean;
  is_verified: boolean;
}

interface AuthContextType {
  user: User | null;
  tools: string[];
  isAuthenticated: boolean;
  loading: boolean;
  hasTool: (toolId: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    passwordConfirm: string,
    firstname: string,
    lastname: string
  ) => Promise<void>;
  inviteUser: (email: string, role: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tools, setTools] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      const userData = await apiClient.get_me_auth_me_get();
      setUser(userData as User);
      // Fetch tools in parallel
      try {
        const myTools = await toolsApi.myTools();
        setTools(myTools);
      } catch {
        setTools([]);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      localStorage.removeItem("access_token");
      setUser(null);
      setTools([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const hasTool = (toolId: string): boolean => {
    if (user?.role === "admin") return true;
    return tools.includes(toolId);
  };

  const login = async (email: string, password: string) => {
    // Il refresh token non è più nel body: viene impostato dal backend come
    // cookie HttpOnly. In localStorage resta solo l'access token.
    const response = await axiosInstance.post("/auth/login", { email, password });
    const { access_token } = response.data;
    localStorage.setItem("access_token", access_token);
    await fetchUser();
  };

  const register = async (
    email: string,
    password: string,
    passwordConfirm: string,
    firstname: string,
    lastname: string
  ) => {
    const registerBody = new UserRegister({
      email,
      password,
      password_confirm: passwordConfirm,
      firstname,
      lastname,
    });
    await apiClient.register_auth_register_post(registerBody);
  };

  const inviteUser = async (email: string, role: string) => {
    await axiosInstance.post("/admin/invite", { email, role });
  };

  const logout = async () => {
    // Il backend legge il refresh token dal cookie HttpOnly, non serve body.
    try {
      await axiosInstance.post("/auth/logout");
    } catch {}
    localStorage.removeItem("access_token");
    setUser(null);
    setTools([]);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tools,
        isAuthenticated: !!user,
        loading,
        hasTool,
        login,
        register,
        inviteUser,
        refreshUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
